/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';

import { jsx } from '@atlaskit/css';
import {
	DefaultExtensionProvider,
	type ExtensionManifest,
	type Parameters,
} from '@atlaskit/editor-common/extensions';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	createSkillNode,
	ROVO_SKILL_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
} from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAnalyticsPropertiesContext } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import {
	defaultIcon,
	skillIcon,
	type SkillIconName,
} from '@atlassian/rovo-platform-ui-components/skills/skill-icons';
import { SkillTag, type RovoSkillTagProps } from '@atlassian/rovo-platform-ui-components/skills/SkillTag';
import { type Skill } from '@atlassian/rovo-platform-ui-components/skills/types';
import { ConnectedSkillTag } from '@atlassian/rovo-skills/ui/connected-skill-tag';

const getSkillIcon = (iconName?: SkillIconName) =>
	iconName && skillIcon[iconName] ? skillIcon[iconName] : defaultIcon;

/**
 * Creates the `nodes.skillTag` definition used by extension manifests to render
 * skill chip inline extensions. This is the **renderer only** — it does not
 * include quick-insert items and therefore does not need the skills list.
 *
 * Extracted so it can be shared between the full `useSkillsManifest` (which
 * also registers quick-insert items) and the lightweight
 * `useSkillTagExtensionProvider` (used as a Suspense fallback so skill chips
 * render immediately without waiting for the skills Relay query).
 */
export const createSkillTagNodeRenderer = (siteId?: string) => ({
	skillTag: {
		type: 'inlineExtension' as const,
		render:
			async (): Promise<
				(props: { node: { parameters?: Record<string, unknown> } }) => React.ReactElement | null
			> =>
			({ node }: { node: { parameters?: Record<string, unknown> } }) => {
				const params = node.parameters;
				if (!params) {
					return null;
				}
				// Use ConnectedSkillTag to fetch and render full metadata so the pill matches the typeahead style.
				if (
					siteId &&
					!params.color &&
					!params.iconName &&
					FeatureGates.getExperimentValue(
						'enable_rovo_static_prompt_file_uploads',
						'isEnabled',
						false,
					)
				) {
					return <ConnectedSkillTag slug={params.slug as string} siteId={siteId} />;
				}
				return <SkillTag {...(params as RovoSkillTagProps)} />;
			},
	},
});

/**
 * Provides a minimal extension provider that **only** registers the skill chip
 * node renderer — no quick-insert items, no skills data dependency.
 *
 * This is designed to be passed as `extensionProviders` to the Suspense
 * **fallback** editor so that skill chip `inlineExtension` nodes embedded in
 * `defaultValue` ADF render immediately, eliminating the visual lag that
 * occurred when the fallback editor had no extension providers and could not
 * render the chip until the full `ComposableEditorWithSkills` mounted.
 */
export const useSkillTagExtensionProvider = (
	siteId?: string,
): DefaultExtensionProvider<Parameters>[] => {
	return React.useMemo(() => {
		const manifest: ExtensionManifest = {
			title: 'Skills',
			type: ROVO_SKILL_EXTENSION_TYPE,
			key: ROVO_SKILL_EXTENSION_KEY,
			icons: {
				'16': defaultIcon,
				'24': defaultIcon,
				'48': defaultIcon,
			},
			modules: {
				quickInsert: [],
				nodes: createSkillTagNodeRenderer(siteId),
			},
		};

		return [new DefaultExtensionProvider<Parameters>([manifest])];
	}, [siteId]);
};

const SKILL_DESCRIPTION_CHAR_LIMIT = 150;
const truncateSkillDescription = (description: string | null | undefined): string => {
	if (!description) {
		return '';
	}
	if (description.length <= SKILL_DESCRIPTION_CHAR_LIMIT) {
		return description;
	}
	const slice = description.slice(0, SKILL_DESCRIPTION_CHAR_LIMIT);
	const lastWhitespace = slice.search(/\s\S*$/);

	const truncated = lastWhitespace > 0 ? slice.slice(0, lastWhitespace) : slice;
	return `${truncated.trimEnd()}…`;
};

const skillsEditorMessages = defineMessages({
	betaLabel: {
		id: 'conversation-assistant-chat-prompt-input.skills-editor-extension.beta-label',
		defaultMessage: 'Beta',
		description: 'Beta badge label shown next to admin skill actions in the slash command menu',
	},
});

const adminSkillBetaLozenge = (
	<Lozenge appearance="discovery">
		<FormattedMessage {...skillsEditorMessages.betaLabel} />
	</Lozenge>
);

type UseSkillsManifestProps = {
	skills: Skill[];
	isConversationAgentic: boolean;
	promptHasSkill: boolean;
	enableSkills?: boolean;
	siteId?: string;
};

export const useSkillsManifest = ({
	skills,
	isConversationAgentic,
	promptHasSkill,
	enableSkills = true,
	siteId,
}: UseSkillsManifestProps): DefaultExtensionProvider<Skill>[] | undefined => {
	const { sendAnalyticsEvent } = useAnalytics();
	// Skill selection source is recorded on the AnalyticsPropertiesContext so the chat
	// input can attach it to the next `aiResult viewed` event without needing a
	// per-input store. The provider is mounted once per chat instance, scoping the value
	// to the input that consumes it.
	const { setAnalyticsPropertiesWithoutRerender } = useAnalyticsPropertiesContext();

	const createManifest = React.useCallback(
		(skills: Skill[]) => {
			const manifest: ExtensionManifest<Skill> = {
				title: 'Skills',
				type: ROVO_SKILL_EXTENSION_TYPE,
				key: ROVO_SKILL_EXTENSION_KEY,
				categories: ['Skills'],
				icons: {
					'16': defaultIcon,
					'24': defaultIcon,
					'48': defaultIcon,
				},
				modules: {
					quickInsert: skills.map((skill) => {
						const { slug, description, name, product, iconName, tags } = skill;
						const lozenge =
							tags?.some((t) => t.toLowerCase() === 'beta') &&
							fg('rovo_chat_editor_admin_skill_beta_lozenge_enabled')
								? adminSkillBetaLozenge
								: undefined;
						return {
							key: slug,
							keywords: ['skills', 'skill', slug, product ?? ''],
							// 1 is the default priority
							priority: 99,
							title: name,
							description: truncateSkillDescription(description),
							icon: getSkillIcon(iconName),
							lozenge,
							action: async () => {
								sendAnalyticsEvent({
									eventType: 'ui',
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'rovoChatSkillSelected',
									attributes: {
										slashCommand: slug,
										skillProductKey: product,
										skillSelectionSource: 'editorTypeahead',
									},
								});
								setAnalyticsPropertiesWithoutRerender({
									skillSelectionSource: 'editorTypeahead',
								});
								return createSkillNode(skill);
							},
						};
					}),
					nodes: createSkillTagNodeRenderer(siteId),
				},
			};

			return manifest;
		},
		[sendAnalyticsEvent, setAnalyticsPropertiesWithoutRerender, siteId],
	);

	const manifestProvider = React.useMemo(() => {
		// Return undefined if skills are disabled
		if (!enableSkills) {
			return undefined;
		}
		// skills are only applicable to non-agentic conversations
		// remove skills if a skill is already present in the prompt until chaining is allowed
		if ((isConversationAgentic || promptHasSkill) && fg('rovo_chat_enable_skills_ui_m1')) {
			return [new DefaultExtensionProvider([createManifest([])])];
		} else if (fg('rovo_chat_enable_skills_ui_m1')) {
			return [new DefaultExtensionProvider([createManifest(skills)])];
		}
		return undefined;
	}, [isConversationAgentic, promptHasSkill, createManifest, enableSkills, skills]);

	return manifestProvider;
};
